import { type ReactNode } from 'react'
import { cn } from '../../lib/utils'

export interface AlertProps {
  variant?: 'success' | 'error' | 'warning' | 'info'
  title?: string
  children: ReactNode
  className?: string
}

export function Alert({
  variant = 'info',
  title,
  children,
  className,
}: AlertProps) {
  const variants = {
    success: {
      container: 'bg-[var(--success-muted)]/30 border-[var(--success-muted)]',
      icon: 'text-[var(--success)]',
      title: 'text-[var(--success)]',
      text: 'text-[var(--success)]',
    },
    error: {
      container: 'bg-[var(--error-muted)]/30 border-[var(--error-muted)]',
      icon: 'text-[var(--error)]',
      title: 'text-[var(--error)]',
      text: 'text-[var(--error)]',
    },
    warning: {
      container: 'bg-[var(--warning-muted)]/30 border-[var(--warning-muted)]',
      icon: 'text-[var(--warning)]',
      title: 'text-[var(--warning)]',
      text: 'text-[var(--warning)]',
    },
    info: {
      container: 'bg-[var(--info-muted)]/30 border-[var(--info-muted)]',
      icon: 'text-[var(--info)]',
      title: 'text-[var(--info)]',
      text: 'text-[var(--info)]',
    },
  }

  const icons = {
    success: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    info: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
  }

  const styles = variants[variant]

  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border p-4',
        styles.container,
        className
      )}
    >
      <div className={cn('flex-shrink-0', styles.icon)}>{icons[variant]}</div>
      <div className="flex-1">
        {title && (
          <h3 className={cn('text-sm font-medium', styles.title)}>{title}</h3>
        )}
        <div className={cn('text-sm', styles.text, title && 'mt-1')}>
          {children}
        </div>
      </div>
    </div>
  )
}
